import { format } from 'date-fns';
import FileSaver from 'file-saver';
import { LatLngExpression } from 'leaflet';
import { DataSourceModel, ExploreSchema, ExploreVector, GeomShape, LocationShapesSourceTypes, MeshResponseDataRaw, ModelTypes, OrderRequestItem, OrganisationDetails, UserRoles } from "../core/global";
import { roleOptions } from '../data/form-shapes';

import { Feature, Geometry } from 'geojson';
import { parse } from 'wellknown';
import { LayerDescription, ModelShape } from '../data/layers';


export const downloadFile = async (data: string, fileName: string, showDate: boolean = true): Promise<any> => {
  if (typeof data === 'string') {
    const blob = new Blob([data], { type: 'text/plain;charset=utf-8' })
    const date = new Date();
    const useDate = format(date, 'dd MMMM yyyy HH:mm:ss');
    await FileSaver.saveAs(
      blob,
      showDate ? `${useDate} ${fileName}.csv` : `${fileName}.csv`,
    )
  }

  return true
}

export const getBboxAreaKm2 = (bounds: {
  min: { lat: number | string; lng: number | string };
  max: { lat: number | string; lng: number | string };
}) => {
  const { min, max } = bounds;

  const toRad = (deg: number) => (deg * Math.PI) / 180;

  const lat1 = Number(min.lat);
  const lat2 = Number(max.lat);
  const lon1 = Number(min.lng);
  const lon2 = Number(max.lng);

  const avgLat = (lat1 + lat2) / 2;

  // constants
  const KM_PER_DEG_LAT = 111.32;

  const latDiff = lat2 - lat1;
  const lonDiff = lon2 - lon1;

  const heightKm = latDiff * KM_PER_DEG_LAT;
  const widthKm = lonDiff * KM_PER_DEG_LAT * Math.cos(toRad(avgLat));

  return Math.abs(widthKm * heightKm);
};

type QueryValue = string | number | boolean | null | undefined | Array<string | number | boolean>;

export const buildUrl = (
  baseUrl: string,
  endPoint: string,
  query?: Record<string, QueryValue>
): string => {
  if (!query) return baseUrl;

  const params = new URLSearchParams();

  Object.entries(query).forEach(([key, value]) => {
    if (value === null || value === undefined) return;

    if (Array.isArray(value)) {
      value.forEach(v => params.append(key, String(v)));
    } else {
      params.append(key, String(value));
    }
  });

  const queryString = params.toString();

  if (!queryString) return baseUrl;

  return `${baseUrl}${endPoint}${baseUrl.includes('?') ? '&' : '?'}${queryString}`;
};

export const convertGeomToGeoJSON = (geom: string, properties: any = {}): Feature => {
  try {
    const geoJSON: Geometry = parse(geom) as Geometry;
    const feature: Feature = {
      type: 'Feature',
      geometry: geoJSON,
      properties
    };
    return feature;
  } catch (error) {
    console.error('Error converting geometry to GeoJSON:', error);
    throw error;
  }
};

export const convertResponseToGeoJson = (data: MeshResponseDataRaw[]): GeoJSON.Feature[] => {
  if (data.length > 0) {
    // @ts-ignore
    const result: GeoJSON.Feature[] | null = data.map((featureData) => {
      const {features, id, order_id, centroid, sqm, coords, wkb_geometry, provider, model, version, geography } = featureData
      let geomObject: Geometry | null = null
      if ((wkb_geometry || '').indexOf('POLYGON') !== -1 || (wkb_geometry || '').indexOf('MULTIPOLYGON') !== -1) {
        const coordinates: string[][] = (wkb_geometry || '').replaceAll(/(MULTIPOLYGON)|(POLYGON)|[()]/gi, '').split(',').map( i => i.split(' '))
        geomObject = {
          type: 'MultiPolygon',
          // @ts-ignore
          coordinates: [[[...coordinates]]],
        }
      } else if ((wkb_geometry || '').indexOf('POINT') !== -1) {
        const coordinates: string[] = (wkb_geometry || '').replaceAll(/(POINT)|[()]/gi, '').split(' ')
        geomObject = {
          type: 'Point',
          // @ts-ignore
          coordinates: [...coordinates],
        }
      }
      if (geomObject === null) {
        return null
      }

      const useFeatures = {...features}
      const feature: GeoJSON.Feature = {
        type: 'Feature',
        geometry: geomObject,
        properties: {
          ...useFeatures,
          order_id,
          id,
          centroid,
          coords,
          provider,
          version,
          model, 
          geography,
          sqm
        },
      };
      return feature
    })
    return result?.filter( f => f !== null) as GeoJSON.Feature[]
  } else {
    return []
  }
}

// TODO: use GEOJSON parser - https://www.npmjs.com/package/geojson
export const convertCSVToGeoJson = (data: [number, number][], fullGeoJson = false): (GeoJSON.Feature[] | GeoJSON.FeatureCollection) => {
  let result: GeoJSON.Feature[] = []
  if (data.length > 0) {
    const isPolygon = data.length > 1 && data[0].join('') === data[data.length - 1].join('')
    if (isPolygon) {
      const feature: GeoJSON.Feature = {
        type: 'Feature',
        geometry: {
          type: 'MultiPolygon',
          // @ts-ignore
          coordinates: [[[...data]]],
        },
        properties: {},
      };
  
      result = [feature]
    } else {
      // Assuming it's a line
      const feature: GeoJSON.Feature = {
        type: 'Feature',
        geometry: {
          type: 'LineString',
          coordinates: [...data],
        },
        properties: {},
      };
  
      result = [feature]
    }
    
  }
  if (fullGeoJson) {
    return {
      "type": "FeatureCollection",
      "features": result
    }
  }
  return result
}

export function findClosestValue(arr: number[], target: number, returnIndex = false) {
  if (arr.length === 0) return null; // Return null if the array is empty

  // Initialize the closest value as the first element of the array
  let closest = arr[0];
  let index = 0
  // Iterate through the array to find the closest value
  for (let i = 1; i < arr.length; i++) {
    // Update closest if the current element is closer to the target
    if (Math.abs(arr[i] - target) < Math.abs(closest - target)) {
      closest = arr[i];
      index = i
    }
  }
  if (returnIndex) return index
  return closest;
}

type GetValue = (val: any) => any

export const getItemByKeyValue = (
  items: any[] | undefined | null,
  key: string,
  value: string | number | boolean | undefined | null,
  returnIndex = false,
  ignoreCase = true,
  getValue: null | GetValue = null,
  returnAllMatches: boolean = false,
): any => {
  if (
    typeof value === 'undefined' ||
    items === null ||
    typeof items === 'undefined'
  ) {
    return -1
  }

  const searchVal = ignoreCase
    ? JSON.stringify(value).toLowerCase()
    : JSON.stringify(value)

  let indexCounter = -1

  const found = items[returnAllMatches? 'filter': 'find']((item, index) => {
    let res
    const useItem = getValue ? getValue(item) : item
    if (ignoreCase) {
      res =
        typeof useItem[key] !== 'undefined' &&
        JSON.stringify(useItem[key]).toLowerCase() === searchVal
    } else {
      res =
        typeof useItem[key] !== 'undefined' &&
        JSON.stringify(useItem[key]) === searchVal
    }

    if (res) {
      indexCounter = index
    }

    return res
  })

  if (returnIndex) {
    return indexCounter
  }
  if (returnAllMatches && found.length === 0) {
    return -1
  }
  return  found || -1
}

export const getFileType = (file: File | null): LocationShapesSourceTypes => {
  if (file === null) return "unknown"
  
  const {name, type} = file
  const useName = (name || '').toLowerCase()
  if (type === 'text/csv') {
    return 'csv-file'
  }
  if (useName.indexOf('.kml') !== -1) {
    return 'kml-file'
  }
  if (useName.indexOf('.shp') !== -1
  || useName.indexOf('.dbf') !== -1
  || useName.indexOf('.shx') !== -1
  ) {
    return 'shape-file'
  }
  if (useName.indexOf('.prj') !== -1) {
    return 'prj-file'
  }
  if (useName.indexOf('.json') !== -1) {
    return 'json-file'
  }
  if (useName.indexOf('.jpg') !== -1 || useName.indexOf('.jpeg') !== -1) {
    return 'jpg-file'
  }
  if (useName.indexOf('.png') !== -1 ) {
    return 'png-file'
  }
  return "unknown"
}



export function getPercentageDistribution(arr: number[]): { [key: number]: number } {
  const total = arr.length;
  
  // Calculate the count distribution
  const countDistribution = arr.reduce((acc, num) => {
      acc[num] = (acc[num] || 0) + 1;
      return acc;
  }, {} as { [key: number]: number });

  // Convert the counts to percentages
  const percentageDistribution: { [key: number]: number } = {};
  for (const key in countDistribution) {
      percentageDistribution[parseFloat(key)] = (countDistribution[key] / total) * 100;
  }


  // Convert the counts to percentages and group them by 5% intervals
  const groupedDistribution: { [key: string]: number } = {};

  for (const key in countDistribution) {
    const roundedKey = Math.round(Number(key))
    groupedDistribution[roundedKey] = groupedDistribution[roundedKey] ? groupedDistribution[roundedKey] + percentageDistribution[key] : percentageDistribution[key]
    
    // const percentage = (countDistribution[key] / total) * 100;

    //   // Determine the 5% group this percentage falls into
    //   const group = `${Math.floor(percentage / 5) * 5}-${Math.floor(percentage / 5) * 5 + 5}%`;

    //   // Accumulate the percentage into the appropriate group
    //   groupedDistribution[group] = (groupedDistribution[group] || 0) + percentage;
  }
  return groupedDistribution;
}

export const getMaxOrMinFeature = (arr: any[], key: string, type: string) => {
  if (arr.length === 0) return null;
  const accessor = (item: any) => {
    if (Object.prototype.hasOwnProperty.call(item, 'features')) {
      const {features} = item
      if (Object.prototype.hasOwnProperty.call(features, key)) {
        return features[key]
      }
    }
    return null
  }

  return arr.reduce((prev, current) => {
    if (type === 'max') {
      return (accessor(current) > accessor(prev) ? current : prev);
    }
    return (accessor(current) < accessor(prev) ? current : prev);
  });
}

export const getTop10MaxOrMinFeatures = (arr: any[], key: string, type: 'max' | 'min', slice = 10) => {
  if (arr.length === 0) return [];

  // Accessor function to retrieve the value by key
  const accessor = (item: any) => {
    if (Object.prototype.hasOwnProperty.call(item, 'features')) {
      const { features } = item;
      if (Object.prototype.hasOwnProperty.call(features, key)) {
        return features[key];
      }
    }
    return null;
  };

  // Sort array based on type (min or max)
  const sortedArray = arr
    .filter(item => accessor(item) !== null) // Filter out items where key is not found
    .sort((a, b) => {
      const aValue = accessor(a);
      const bValue = accessor(b);
      if (aValue === null || bValue === null) return 0;
      return type === 'max' ? bValue - aValue : aValue - bValue;
    });

  return sortedArray.slice(0, slice);
};


export const findOrganisation = (userOrganisation: null | OrganisationDetails[] | undefined, searchId: number | string | null) => {
  if (userOrganisation && Array.isArray(userOrganisation)) {
    return userOrganisation.find(({ id }: OrganisationDetails) => id === searchId)
  }
  return undefined
}


export const isMemberOf = (userOrganisation: null | OrganisationDetails[] | undefined, searchId: number | string | null) => {
  const found = findOrganisation(userOrganisation, searchId)
  return typeof found !== 'undefined'
}

// Define the configuration object for password strength
const passwordConfig: PasswordConfig = {
  minLength: 6,
  maxLength: 99,
  requireUppercase: false,
  requireLowercase: false,
  requireNumbers: true,
  requireSpecialCharacters: false,
  allowedSpecialCharacters: "!@#$%^&*()_+[]{}|;:,.<>?/~`"
};


type PasswordConfig = {
  minLength: number;
  maxLength: number;
  requireUppercase: boolean;
  requireLowercase: boolean;
  requireNumbers: boolean;
  requireSpecialCharacters: boolean;
  allowedSpecialCharacters: string;
};

type ValidationResult = {
  isValid: boolean;
  failedCriteria: string[];
};

export const validatePassword = (password: string, config = passwordConfig): ValidationResult => {
  const failedCriteria: string[] = [];

  // Check for minimum length
  if (password.length < config.minLength) {
      failedCriteria.push(`Password must be at least ${config.minLength} characters long.`);
  }

  // Check for maximum length
  if (password.length > config.maxLength) {
      failedCriteria.push(`Password must not exceed ${config.maxLength} characters.`);
  }

  // Check for uppercase letters
  if (config.requireUppercase && !/[A-Z]/.test(password)) {
      failedCriteria.push("Password must contain at least one uppercase letter.");
  }

  // Check for lowercase letters
  if (config.requireLowercase && !/[a-z]/.test(password)) {
      failedCriteria.push("Password must contain at least one lowercase letter.");
  }

  // Check for numbers
  if (config.requireNumbers && !/[0-9]/.test(password)) {
      failedCriteria.push("Password must contain at least one number.");
  }

  // Check for special characters
  if (config.requireSpecialCharacters) {
      const specialCharsRegex = new RegExp(`[${config.allowedSpecialCharacters.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&')}]`);
      if (!specialCharsRegex.test(password)) {
          failedCriteria.push(`Password must contain at least one special character from: ${config.allowedSpecialCharacters}`);
      }
  }

  return {
    isValid: failedCriteria.length === 0,
    failedCriteria
  }
}

export const getAccountLevelLabel = (level: number | null | undefined) => {
  if (!level) {
    return 'Unknown'
  }
  if (level <= 10) {
    return "Pay-As-You-Go"
    // return 'Free'  
  }
  if (level === 50) {
    return 'Unlimited access'
  }
  if (level === 90) {
    return 'downgraded super-admin'
  }
  if (level === 100) {
    return 'Super-admin'
  }
  return 'Unknown'
}

export const displayUserRole = (role: UserRoles | null | undefined) => {
  const useRole = role === null ? 'unknown' :  role
  const found = getItemByKeyValue(roleOptions, 'key', useRole)
  if (found) {
    return found.value
  }
  return useRole 
}

export const getLatLonFromString = (coords: string | undefined | null) => {
  if (coords && typeof coords === 'string') {
    const c = coords.split(' ')
    if (c.length === 2) {
      const [lat, lon] = c
      return {
        lat: Number(lat), 
        lon: Number(lon)
      }
    }
  }
  return {
    lat: null, 
    lon: null
  }
}



export const roundCoord = (coord: number | string, places: number) => {
  return Number(window.parseFloat(`${coord}`).toFixed(places));
}

export const addPoints = (existingPoints: LatLngExpression[], newPoints: LatLngExpression[]): LatLngExpression[] => {
  const uniquePoints = existingPoints.map(point => JSON.stringify(point)); // Convert points to strings for comparison
  const updatedPoints = [...existingPoints];

  newPoints.forEach(point => {
    if (!uniquePoints.includes(JSON.stringify(point))) {
      updatedPoints.push(point);
    }
  });

  return updatedPoints;
}

export const removePoints = (existingPoints: LatLngExpression[], pointsToRemove: LatLngExpression[]): LatLngExpression[] => {
  const pointsToRemoveSet = new Set(pointsToRemove.map(point => JSON.stringify(point))); // Convert to strings for fast lookup
  return existingPoints.filter(point => !pointsToRemoveSet.has(JSON.stringify(point)));
}

export const valueIsSet = (val: any | any[]) => {
  const useArr = Array.isArray(val)? [...val] : [val]
  return useArr.find( item => !(typeof item !== "undefined" && item !== null && !isNaN(item))) === -1
}



interface GetMinMaxValuesProps {
  currentLayer: LayerDescription
  data: GeomShape[]
  type: "min" | "max"
  dataSourceModel: DataSourceModel,
}

export const getMinMaxValuesFromGeomShape = ({
  currentLayer,
  data,
  type,
  dataSourceModel,
}: GetMinMaxValuesProps ): Feature[] => {
    if (!dataSourceModel || !currentLayer) return []
    const {model, provider, geography, version} = dataSourceModel
    const {reverse, valueKey} = currentLayer
    const allRows = [...data.filter( r => Object.prototype.hasOwnProperty.call(r, 'features') && r.model === model && r.provider === provider && r.geography === geography && `${r.version}` === `${version}`)]
    
    if (!valueKey) return []

    if (type === 'min') {
      let minValue = getTop10MaxOrMinFeatures([...allRows], valueKey, reverse? 'max' : 'min', 10)
      if (minValue !== null && Array.isArray(minValue)) {
        if (reverse) {
          minValue = [...minValue.reverse()]
        }
          minValue = minValue.map( (item, index) => {
          return {
            ...item,
            features: {
              ...item.features,
              index,
              extreme: true,
              isMinValue: true,
              highlight: true,
            }
          }
        })
        minValue = [...convertResponseToGeoJson(minValue as MeshResponseDataRaw[])]
      }
      return minValue
    } else {
      let maxValue = getTop10MaxOrMinFeatures([...allRows], valueKey, reverse? 'min' : 'max', 10)
      if (maxValue !== null && Array.isArray(maxValue)) {
        if (reverse) {
          maxValue = [...maxValue.reverse()]
        }
        maxValue = maxValue.map( (item, index) => {
          return {
            ...item,
            features: {
              ...item.features,
              extreme: true,
              index,
              isMaxValue: true,
              highlight: true,
            }
          }
        })
        maxValue = [...convertResponseToGeoJson(maxValue as MeshResponseDataRaw[])]
      }
      return maxValue
    }
}


export const delay = (ms: number) => new Promise(res => setTimeout(res, ms));

export const calculateCellResolution = (sqm: number, model: ModelTypes) => {
  if (model && model === "swan") {
    return (Math.sqrt(sqm*7990.5721)*1000)
  }
  return (1.5197 * Math.sqrt(sqm*7375696126.43091))
}


export const getPurchaseRequestsByType = (companyPurchaseRequests: OrderRequestItem[] | null, company_id: number | null, requestStatus?: string[]) => {
  return companyPurchaseRequests?.filter(({status, organisation_id}) => organisation_id === company_id && (requestStatus && requestStatus.includes(status))) 
}


// TODO: a bit pants perhaps, improves this
export const convertDataSourcesResponseToModelShape = (response: any[]): ModelShape | null => {
  if (response && Array.isArray(response) && response.length > 0) {
    const result: ModelShape = {}
    response.forEach( item => {
      const {
        provider,
        tokens_cost_per_year,
        source, 
        model, 
        geography, 
        layers, 
        full_name, 
        attribution, 
        description,
        tiles_domain,
        start_date,
        end_date,
        resolution,
        frequency,
        vertical_reference,
        temporal_reference,
        vector_tiles,
        vectors,
        version,
        has_averages,
        bounds,
        max_lat,
        max_lon,
        min_lat,
        min_lon,
        options,
        regular,
        grid_resolution,
      } = item

      const key = `${provider}-${model}-${geography}-${version}`.toLowerCase()
      result[key] = {
        options,
        bounds,
        max_lat,
        max_lon,
        min_lat,
        min_lon,
        resolution,
        frequency,
        vertical_reference,
        temporal_reference,
        start_date,
        end_date,
        description,
        model,
        provider,
        geography,
        version,
        source,
        vectors,
        vector_tiles: !vector_tiles ? null : vector_tiles.indexOf('http') !== -1 ? vector_tiles : `${tiles_domain}${vector_tiles}`,
        tokens_cost_per_year: tokens_cost_per_year || 0,
        key,
        name: full_name,
        has_averages,
        regular,
        grid_resolution,
        layers: layers.map( (layer: any)  => {
          const {key, 
            min_value, 
            max_value, 
            units, 
            colours, 
            short_name, 
            title, 
            hide, 
            url, 
            data_url, 
            reverse, 
            display_max_value,
            min_value_label: minValueLabel,
            max_value_label: maxValueLabel,
            format,
          } = layer
          if (key === "model-grid") {
            return {
              hide,
              title,
              valueKey: key,
              attribution,
              url: `${tiles_domain}${url}`,
            }
          }
          return {
            format,
            minValueLabel,
            maxValueLabel,
            hide,
            title,
            shortName: short_name || '',
            attribution,
            url: (url && url.indexOf('http') !== -1) ? url : `${tiles_domain}${url}`,
            colours: colours ? Object.fromEntries(
              (colours || []).map( (c: string, index: number) => {
                return [String(index), c]

              })
            ) : null,
            dataUrl: data_url ? `${tiles_domain}${data_url}` : null,
            minValue: isNaN(parseFloat(`${min_value}`)) ? null : min_value, 
            maxValue: isNaN(parseFloat(`${max_value}`)) ? null : max_value, 
            units: units || null,
            minValueKey: `min_${key.replace(/[\s]/gi, '_')}`.toLowerCase(),
            maxValueKey: `max_${key.replace(/[\s]/gi, '_')}`.toLowerCase(),
            // valueKey: key.replace(/[\s]/gi, '_').toLowerCase(),
            valueKey: key,
            reverse: !!reverse,
            displayMaxValue: typeof display_max_value === 'number' ? display_max_value : null,
            show_bands: layer.show_bands === true,
          };
        }) || []
      }
    })
    return result
  }
  return null
}


// export const interpolateValues = (lat: number | null, lon: number | null, points: {lat: number | null, lon: number | null, val: number | null}[]): number | null => {
//   if (!points || points.length < 4 || typeof lat !== 'number' || typeof lon !== 'number') return null;

// };

export const interpolateValues = (
  lat: number | null,
  lon: number | null,
  points: { lat: number | null; lon: number | null; val: number | null }[]
): number | null => {
  if (
    !points ||
    points.length < 4 ||
    typeof lat !== 'number' ||
    typeof lon !== 'number'
  ) {
    return null;
  }

  const eps = 1e-6;

  // filter valid points
  const valid = points.filter(
    (p) =>
      typeof p.lat === 'number' &&
      typeof p.lon === 'number' &&
      typeof p.val === 'number'
  ) as { lat: number; lon: number; val: number }[];

  if (valid.length < 4) return null;

  // get unique sorted bounds
  const lats = [...new Set(valid.map((p) => p.lat))].sort((a, b) => a - b);
  const lons = [...new Set(valid.map((p) => p.lon))].sort((a, b) => a - b);

  if (lats.length < 2 || lons.length < 2) {
    return idwFallback(lat, lon, valid);
  }

  const lat1 = lats[0];
  const lat2 = lats[lats.length - 1];
  const lon1 = lons[0];
  const lon2 = lons[lons.length - 1];

  const find = (la: number, lo: number) =>
    valid.find(
      (p) => Math.abs(p.lat - la) < eps && Math.abs(p.lon - lo) < eps
    )?.val ?? null;

  const Q11 = find(lat1, lon1);
  const Q21 = find(lat1, lon2);
  const Q12 = find(lat2, lon1);
  const Q22 = find(lat2, lon2);

  // ✅ if full rectangle exists → bilinear interpolation
  if ([Q11, Q21, Q12, Q22].every((v) => typeof v === 'number')) {
    const denom = (lon2 - lon1) * (lat2 - lat1);
    if (denom === 0) return Q11 as number;

    return (
      ((Q11 as number) * (lon2 - lon) * (lat2 - lat) +
        (Q21 as number) * (lon - lon1) * (lat2 - lat) +
        (Q12 as number) * (lon2 - lon) * (lat - lat1) +
        (Q22 as number) * (lon - lon1) * (lat - lat1)) /
      denom
    );
  }

  // ⚠️ fallback → IDW
  return idwFallback(lat, lon, valid);
};

const idwFallback = (
  lat: number,
  lon: number,
  points: { lat: number; lon: number; val: number }[]
): number => {
  let num = 0;
  let den = 0;

  for (const p of points) {
    const d = Math.sqrt((p.lat - lat) ** 2 + (p.lon - lon) ** 2);

    // exact match shortcut
    if (d < 1e-12) return p.val;

    const w = 1 / d;

    num += w * p.val;
    den += w;
  }

  return den === 0 ? points[0].val : num / den;
};


export const applyTimeFrom = (target: Date | null, source: Date | null): Date | null => {
  if (!target || !source) return target;

  const updated = new Date(target);
  updated.setHours(
    source.getHours(),
    source.getMinutes(),
    source.getSeconds(),
    source.getMilliseconds()
  );

  return updated;
};

export const isNumber = <T>(v: T): v is NonNullable<T> =>
  v != null && v !== "" && !isNaN(Number(v));




  type ColumnKey = { key: string; title: string };

  const toColumnKey = ({ key, name }: ExploreVector): ColumnKey => ({ key, title: name });
  
  // Expand one array vector into one vector per key (empty if it fails the guard).
  const expandArrayVector = (item: ExploreVector): ExploreVector[] => {
    const { keys, type, name, height, min, max, isArray, base_key, ...props } = item;
    if (!(type && isArray && keys && keys.length > 0)) return [];
    return keys.map((k, idx) => {
      const _min = min && Array.isArray(min) && min.length > idx ? min[idx] : 0;
      const _max = max && Array.isArray(max) && max.length > idx ? max[idx] : 0;
      const _height = height && Array.isArray(height) && height.length > idx ? height[idx] : 0;
      return {
        ...props,
        name: `${name} at ${_height} m`,
        key: `${k}`,
        min: _min,
        max: _max,
      };
    });
  };
  
  // Keep every vector; expand it only if it's an array vector.
  const expandInPlace = (v: ExploreVector): ExploreVector[] =>
    v.isArray ? expandArrayVector(v) : [v];
  
  export function getExploreColumnKeys(schema: ExploreSchema | null): ColumnKey[];
  export function getExploreColumnKeys(schema: ExploreSchema | null, mode: 'all'): ColumnKey[];
  export function getExploreColumnKeys(
    schema: ExploreSchema | null,
    mode: 'split',
  ): { directional: ColumnKey[]; nonDirectional: ColumnKey[] };
  
  export function getExploreColumnKeys(
    schema: ExploreSchema | null,
    mode: 'all' | 'split' = 'all',
  ) {
    if (schema === null) {
      return mode === 'split' ? { directional: [], nonDirectional: [] } : [];
    }
  
    const { vectors } = schema;
  
    if (mode === 'split') {
      const directional = vectors.filter((v) => v.directional).flatMap(expandInPlace).map(toColumnKey);
      const nonDirectional = vectors.filter((v) => !v.directional).flatMap(expandInPlace).map(toColumnKey);
      return { directional, nonDirectional };
    }
  
    // 'all' — preserve original vector order, expanding array vectors in place
    return vectors.flatMap(expandInPlace).map(toColumnKey);
  }